import React, {useRef, useState} from 'react';
import 'react-calendar/dist/Calendar.css';
import {useRecoilValue} from 'recoil';
import {localeState} from 'utils/Atoms';
import {View} from 'react-calendar/dist/cjs/shared/types';
import moment from 'moment-timezone';
import {toDateOrUndefined} from 'utils/toDate';
import {
  StyledDatePicker,
  nextArrowIcon,
  prevArrowIcon,
} from './CommonDatePickerStyle';

/**
 * 공통 날짜 선택기 컴포넌트의 Props 인터페이스
 * @property {Date} [startDate] - 시작 날짜
 * @property {(date: any) => void} [setStartDate] - 시작 날짜 설정 함수
 * @property {React.RefObject<HTMLDivElement>} [innerRef] - 내부 참조 객체
 * @property {any} [setOpen] - 열림 상태 설정 함수
 * @property {any} [value] - 선택된 값
 * @property {any} [setValue] - 값 설정 함수
 * @property {() => void} [onChange] - 변경 이벤트 핸들러
 * @property {any} [maxDate] - 최대 날짜
 * @property {any} [minDate] - 최소 날짜
 */
interface ICommonDatePicker {
  startDate?: Date;
  setStartDate?: (date: any) => void;
  innerRef?: React.RefObject<HTMLDivElement> | undefined;
  setOpen?: any;
  value?: any;
  setValue?: any;
  onChange?: () => void;
  maxDate?: any;
  minDate?: any;
}

/**
 * 공통 날짜 선택기 컴포넌트
 * 날짜를 선택할 수 있는 공통 컴포넌트입니다.
 * @param {ICommonDatePicker} props - 날짜 선택기 컴포넌트의 props
 * @returns {JSX.Element} 공통 날짜 선택기 컴포넌트
 */
const CommonDatePicker = ({
  startDate = new Date(),
  setStartDate,
  innerRef,
  setOpen,
  value,
  setValue,
  onChange,
  maxDate,
  minDate,
}: ICommonDatePicker) => {
  const locale = useRecoilValue(localeState);

  const [view, setView] = useState<View>('month');

  const ref = useRef(null);

  /**
   * react-calendar 는 value 만 내부적으로 Date 로 변환하고
   * minDate / maxDate 는 그대로 date-utils 에 넘겨 Date 가 아니면 렌더 중 예외를 던진다.
   * API 응답의 ISO 문자열이 그대로 흘러들어오는 경로가 있으므로 세 값 모두 여기서 정규화한다.
   */
  const normalizedValue = toDateOrUndefined(value);
  const normalizedMinDate = toDateOrUndefined(minDate);
  const normalizedMaxDate = toDateOrUndefined(maxDate);

  return (
    <StyledDatePicker
      className="common_date_picker"
      // inputRef={innerRef}
      inputRef={ref}
      onChange={onChange}
      view={view}
      value={normalizedValue}
      locale={locale}
      prevLabel={prevArrowIcon()}
      nextLabel={nextArrowIcon()}
      next2Label={null}
      prev2Label={null}
      onClickYear={() => {
        setView('year');
      }}
      onClickMonth={() => {
        setView('month');
      }}
      onClickDay={(e: any) => {
        setValue(e);
        setOpen && setOpen(false);
      }}
      // calendarType="US"
      formatYear={(locale: any, date: any) => moment(date).format('YYYY')}
      formatDay={(locale: any, date: any) =>
        date.toLocaleString('en', {day: 'numeric'})
      }
      formatShortWeekday={(locale: any, date: any) =>
        locale !== 'ko-KR'
          ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()]
          : ['일', '월', '화', '수', '목', '금', '토'][date.getDay()]
      }
      onDrillUp={() => {
        setView('decade');
        // setView('year');
      }}
      $view={view}
      maxDate={normalizedMaxDate}
      minDate={normalizedMinDate}
    />
  );
};

export default CommonDatePicker;
